/**
 * `drive.*` host predicate — the single place that decides whether the browser
 * is on the studio / client file-upload surface.
 *
 * Why this is a shared module and not another inline `hostname === '…'`
 * ---------------------------------------------------------------------
 * Exactly the reasoning `laMirageInHost.ts` gives for itself: the same question
 * is asked at more than one branch point in `App.tsx`, and the three
 * hand-rolled copies of `isCustomWeddingDomain` are why that predicate is now
 * impossible to change safely. The reserved-subdomain list in this repo is
 * copied FOUR times, one of them under a different name, and that is how
 * `drive` came to be missing from all of them.
 *
 * 🔴 ORDER MATTERS, and getting it wrong is silent.
 * `isLaMirageInHost` matches the apex AND ANY SUBDOMAIN — deliberately, so a
 * future `staging.la-mirage.in` cannot fall through to the wedding-website
 * renderer. But that means it also matches `drive.la-mirage.in`. So the drive
 * check MUST run BEFORE the la-mirage.in short-circuit in `AppContent`, or the
 * upload page silently renders the booking funnel instead.
 *
 * Two hosts, one page:
 *   - `drive.lamirage.co`  — the new one.
 *   - `drive.la-mirage.in` — what studios already have. It was a CNAME to Zoho
 *     WorkDrive until this feature replaced it, so links already in circulation
 *     keep working and land on our uploader instead of a Zoho login.
 *
 * ⚠️ Both hosts must also be in the R2 bucket's CORS `AllowedOrigins`, or the
 * browser cannot read the `ETag` header and every upload fails at the final
 * step, after transferring every byte. See scripts/files/set-r2-cors.cjs.
 */

/** Exact hosts. NOT a suffix match — `drive.la-mirage.in.evil.com` is not us. */
export const DRIVE_HOSTS = ['drive.lamirage.co', 'drive.la-mirage.in'] as const;

/** Normalise a raw host: strip a numeric `:port` and a trailing FQDN dot. */
function normalizeHost(raw: unknown): string {
  if (typeof raw !== 'string') return '';
  let host = raw.trim().toLowerCase();
  if (host === '') return '';
  if (host.startsWith('[')) return host;                 // IPv6 literal, never ours
  const lastColon = host.lastIndexOf(':');
  if (lastColon !== -1 && /^\d+$/.test(host.slice(lastColon + 1))) host = host.slice(0, lastColon);
  if (host.endsWith('.')) host = host.slice(0, -1);      // `drive.lamirage.co.` is the same host
  return host;
}

/** True on either upload host. False everywhere else, including lookalikes. */
export function isDriveHost(hostname: string): boolean {
  const host = normalizeHost(hostname);
  return host !== '' && (DRIVE_HOSTS as readonly string[]).includes(host);
}

export default isDriveHost;
